import { lazy, type ReactElement } from 'react';
import { Route, createRoutesFromElements, Navigate, useLocation, useParams } from 'react-router-dom';

import App from './App.tsx';
import ProtectedRoute from './components/ProtectedRoute.tsx';
import { getAppBuildProvenance } from './lib/buildProvenance.ts';
import { getDefaultPostLoginPath } from './lib/productCapabilities.ts';
import { WorkspaceSubstrateRouteGuard } from './pages/workspace/WorkspaceSubstrateUnavailablePage.tsx';

/** Post-login default when `?next=` is absent (workspace hub or U-value calculator). */
export const DEFAULT_POST_LOGIN_PATH = getDefaultPostLoginPath();

// Lazy load all pages
const ProfileRoutePage = lazy(() => import('./pages/ProfileRoutePage.tsx'));
const Settings = lazy(() => import('./pages/Settings.tsx'));
const UValueMaterials = lazy(() => import('./pages/UValueMaterials.tsx'));
const DesignOptionDetailPage = lazy(() => import('./pages/DesignOptionDetailPage.tsx'));
const AssessmentCaseViewPage = lazy(() => import('./pages/AssessmentCaseViewPage.tsx'));
const StyleProbePage = lazy(() => import('./pages/dev/StyleProbePage.tsx'));
const ParityPage = lazy(() => import('./pages/dev/ParityPage.tsx'));
const DashboardParityPage = lazy(() => import('./pages/dev/DashboardParityPage.tsx'));
const WorkspaceHubPage = lazy(() => import('./pages/workspace/WorkspaceHubPage.tsx'));
const SandboxHomePage = lazy(() => import('./pages/workspace/SandboxHomePage.tsx'));
const ProjectHomePage = lazy(() => import('./pages/workspace/ProjectHomePage.tsx'));
const ProjectMemoDetailPage = lazy(() => import('./pages/workspace/ProjectMemoDetailPage.tsx'));

// Auth pages
const Login = lazy(() => import('./pages/Login.tsx'));
const SetPassword = lazy(() => import('./pages/SetPassword.tsx'));
const Register = lazy(() => import('./pages/Register.tsx'));
const RegisterSuccess = lazy(() => import('./pages/RegisterSuccess.tsx'));
const VerifyEmailPending = lazy(() => import('./pages/VerifyEmailPending.tsx'));
const UValueCalculatorRoutePage = lazy(() => import('./pages/UValueCalculatorRoutePage.tsx'));

function workspaceSubstrateProtected(page: ReactElement) {
  return (
    <ProtectedRoute requiredSystemRole="org_user">
      <WorkspaceSubstrateRouteGuard>{page}</WorkspaceSubstrateRouteGuard>
    </ProtectedRoute>
  );
}

// JSON health check component (SPA: first document is still index.html until JS runs; use /build-info.json for curl-only proof)
const Health = () => {
  const payload = getAppBuildProvenance();
  return (
    <pre data-health aria-hidden="true">
      {JSON.stringify(payload, null, 2)}
    </pre>
  );
};

// Redirect helper that preserves query params and hash
function RedirectPreserveSearch({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

function LegacyWorkspaceSandboxRedirect() {
  return <Navigate to="/sandbox" replace />;
}

function LegacyWorkspaceProjectRedirect() {
  const { projectId } = useParams();
  return <Navigate to={`/projects/${encodeURIComponent(projectId ?? '')}`} replace />;
}

export const routes = createRoutesFromElements(
  <>
    {/* Public routes - NO AppShell */}
    <Route path="/_health" element={<Health />} />
    <Route path="/login" element={<Login />} />
    <Route path="/set-password" element={<SetPassword />} />
    <Route path="/auth/register" element={<Register />} />
    <Route path="/auth/register-success" element={<RegisterSuccess />} />
    <Route path="/auth/verify-email" element={<VerifyEmailPending />} />
    <Route path="/u-value/calculator" element={<UValueCalculatorRoutePage />} />
    <Route path="/tools/u-value-calculator" element={<RedirectPreserveSearch to="/u-value/calculator" />} />

    {/* Legacy auth redirects - preserve params */}
    <Route path="/auth/login" element={<RedirectPreserveSearch to="/login" />} />
    <Route path="/auth/reset" element={<RedirectPreserveSearch to="/set-password" />} />
    
    {/* Protected routes - wrapped in AppShell */}
    <Route element={<App />}>
      <Route index element={<Navigate to={DEFAULT_POST_LOGIN_PATH} replace />} />
      <Route path="/workspace" element={workspaceSubstrateProtected(<WorkspaceHubPage />)} />
      <Route path="/sandbox" element={workspaceSubstrateProtected(<SandboxHomePage />)} />
      <Route path="/projects/:projectId" element={workspaceSubstrateProtected(<ProjectHomePage />)} />
      <Route
        path="/projects/:projectId/memos/:controlledIssueId"
        element={workspaceSubstrateProtected(<ProjectMemoDetailPage />)}
      />
      <Route
        path="/workspace/sandbox"
        element={
          <ProtectedRoute requiredSystemRole="org_user">
            <LegacyWorkspaceSandboxRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workspace/project/:projectId"
        element={
          <ProtectedRoute requiredSystemRole="org_user">
            <LegacyWorkspaceProjectRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requiredSystemRole="org_user">
            <Navigate to={DEFAULT_POST_LOGIN_PATH} replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfileRoutePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/u-value/materials"
        element={
          <ProtectedRoute requiredSystemRole="org_user">
            <UValueMaterials />
          </ProtectedRoute>
        }
      />
      <Route
        path="/u-value/design-options/:projectId/:designOptionId"
        element={workspaceSubstrateProtected(<DesignOptionDetailPage />)}
      />
      <Route
        path="/assessments/:assessmentCaseId/published"
        element={workspaceSubstrateProtected(<AssessmentCaseViewPage />)}
      />
      <Route
        path="/assessments/:assessmentCaseId"
        element={workspaceSubstrateProtected(<AssessmentCaseViewPage />)}
      />
      {/* Dev-only style probe */}
      {import.meta.env.DEV && (
        <Route path="/dev/style-probe" element={<StyleProbePage />} />
      )}
      {/* Dev-only parity test */}
      {import.meta.env.DEV && (
        <Route path="/__parity__" element={<ParityPage />} />
      )}
      {/* Dev-only dashboard parity test */}
      {import.meta.env.DEV && (
        <Route path="/__dashboard-parity__" element={<DashboardParityPage />} />
      )}
    </Route>
    
    {/* 404 catch-all - outside AppShell so errors are visible */}
    <Route path="*" element={<div className="min-h-screen flex items-center justify-center"><div className="text-center"><h1 className="text-2xl font-bold">404 - Page Not Found</h1><p className="mt-2 text-gray-600">The page you are looking for does not exist.</p></div></div>} />
  </>
);
